import clsx from 'clsx';
import { Disc3, Eraser, Plus, Trash2, Volume2 } from 'lucide-react';
import { useState } from 'react';
import { registerBuffer } from '../audio/decode';
import { engine } from '../audio/engine';
import { bounceTrack } from '../audio/render';
import { useStore } from '../store/useStore';
import type { WarpMode } from '../types';
import { Meter } from './ui/Meter';
import { Select } from './ui/Controls';

const WARP_OPTIONS: { value: WarpMode; label: string }[] = [
  { value: 'stretch', label: 'Warp: stretch' },
  { value: 'repitch', label: 'Warp: repitch' },
  { value: 'slice', label: 'Warp: slice' },
  { value: 'off', label: 'Warp: off' },
];

/** Always-visible bottom rack: one compact strip per track. */
export function TrackStrip() {
  const project = useStore((s) => s.project);
  const selectedTrackId = useStore((s) => s.selectedTrackId);
  const store = useStore();
  const [bouncing, setBouncing] = useState<string | null>(null);

  const bounce = async (trackId: string) => {
    setBouncing(trackId);
    try {
      const buffer = await bounceTrack(project, trackId, 1);
      const { meta } = registerBuffer(buffer, `${
        project.tracks.find((t) => t.id === trackId)?.name ?? 'Track'
      } bounce`, project.beatsPerBar, { bpm: project.bpm, bounced: true });
      store.addSource(meta);
      store.notify('Track bounced to a new loop — resample away');
    } finally {
      setBouncing(null);
    }
  };

  return (
    <div className="flex gap-2 border-t border-ink-700 bg-ink-900 p-2">
      {project.tracks.map((track) => {
        const active = track.id === (selectedTrackId ?? project.tracks[0]?.id);
        const source = project.sources.find((s) => s.id === track.sourceId);
        return (
          <div
            key={track.id}
            onClick={() => store.setUi({ selectedTrackId: track.id, selectedSteps: [] })}
            className={clsx(
              'flex min-w-0 flex-1 cursor-pointer flex-col gap-1.5 rounded-lg border p-2 transition-colors',
              active ? 'bg-ink-850' : 'border-ink-700 bg-ink-900 hover:border-ink-600',
            )}
            style={active ? { borderColor: track.color } : undefined}
          >
            <div className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: track.color, boxShadow: `0 0 6px ${track.color}` }}
              />
              <input
                className="min-w-0 flex-1 truncate bg-transparent text-[11px] font-semibold outline-none"
                value={track.name}
                onChange={(e) => store.updateTrack(track.id, { name: e.target.value })}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                className={clsx('btn btn-icon !px-1.5 !py-0.5 text-[9px]', track.mute && 'btn-active')}
                onClick={(e) => {
                  e.stopPropagation();
                  store.updateTrack(track.id, { mute: !track.mute });
                }}
              >
                M
              </button>
              <button
                className={clsx('btn btn-icon !px-1.5 !py-0.5 text-[9px]', track.solo && 'btn-active')}
                onClick={(e) => {
                  e.stopPropagation();
                  store.updateTrack(track.id, { solo: !track.solo });
                }}
              >
                S
              </button>
              <button
                className="btn btn-icon !px-1 !py-0.5"
                title="Clear this track — unassigns its loop and pattern"
                disabled={!track.sourceId && !track.patternId}
                onClick={(e) => {
                  e.stopPropagation();
                  store.clearTrack(track.id);
                  store.notify(`${track.name} cleared`);
                }}
              >
                <Eraser size={11} />
              </button>
              <button
                className="btn btn-icon !px-1 !py-0.5"
                title="Remove this track"
                disabled={project.tracks.length <= 1}
                onClick={(e) => {
                  e.stopPropagation();
                  store.removeTrack(track.id);
                }}
              >
                <Trash2 size={11} />
              </button>
            </div>

            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
              <Select
                className="min-w-0 flex-1 !text-[10px]"
                value={track.sourceId ?? ''}
                options={[
                  { value: '', label: '— no loop —' },
                  ...project.sources.map((s) => ({ value: s.id, label: s.name })),
                ]}
                onChange={(v) => store.assignSource(track.id, v || null)}
              />
              <Select
                className="min-w-0 flex-1 !text-[10px]"
                value={track.patternId ?? ''}
                options={[
                  { value: '', label: '— no pattern —' },
                  ...project.patterns.map((p) => ({ value: p.id, label: p.name })),
                ]}
                onChange={(v) => store.assignPattern(track.id, v || null)}
              />
            </div>

            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <Select
                className="!text-[10px]"
                value={track.warp}
                options={WARP_OPTIONS}
                onChange={(v) => store.updateTrack(track.id, { warp: v })}
              />
              {source && (
                <button
                  className={clsx(
                    'btn btn-icon mono shrink-0 !px-1.5 text-[9px]',
                    Math.abs(source.bpm - project.bpm) < 0.05 && 'btn-active',
                  )}
                  title={`Set the project tempo to ${source.bpm.toFixed(1)} BPM, this loop's own tempo`}
                  onClick={() => {
                    store.set((p) => void (p.bpm = Math.round(source.bpm * 100) / 100));
                    store.notify(`Project tempo → ${source.bpm.toFixed(1)} BPM`);
                  }}
                >
                  {source.bpm.toFixed(0)}
                </button>
              )}
              <Volume2 size={11} className="shrink-0 text-ink-500" />
              <input
                type="range"
                className="min-w-0 flex-1"
                min={0}
                max={1.4}
                step={0.01}
                value={track.volume}
                onChange={(e) => store.updateTrack(track.id, { volume: parseFloat(e.target.value) })}
              />
              <Meter
                analyser={engine.trackAnalyser(track.id)}
                className="h-4 w-8 shrink-0 rounded-sm"
              />
              <button
                className="btn btn-icon"
                title="Bounce this track's pattern to a new loop"
                disabled={!source || bouncing !== null}
                onClick={() => void bounce(track.id)}
              >
                <Disc3 size={12} className={bouncing === track.id ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        );
      })}

      <div className="flex flex-col gap-1">
        <button className="btn btn-icon flex-1 px-2" title="Add track" onClick={() => store.addTrack()}>
          <Plus size={14} />
        </button>
        <button
          className="btn btn-icon flex-1 px-2"
          title="Clear every track"
          disabled={!project.tracks.some((t) => t.sourceId || t.patternId)}
          onClick={() => {
            store.clearAllTracks();
            store.notify('All tracks cleared');
          }}
        >
          <Eraser size={14} />
        </button>
      </div>
    </div>
  );
}
